import type {
  DirectorScenePayload,
  DirectorSubmitState,
  Id,
  ScriptDocument,
} from "@supermanus/document-model";

/** Snapshot a program (or fork) scene into a director payload. */
export function buildDirectorPayload(
  doc: ScriptDocument,
  sceneId: Id,
  submitState?: DirectorSubmitState,
): DirectorScenePayload | null {
  const scene = doc.scenes[sceneId];
  if (!scene) return null;
  const cues: DirectorScenePayload["cues"] = {};
  for (const cueId of scene.cueIds) {
    const cue = doc.cues[cueId];
    if (cue) cues[cueId] = { ...cue, visual: { ...cue.visual }, audio: { ...cue.audio } };
  }
  const comments: NonNullable<DirectorScenePayload["comments"]> = {};
  for (const [id, comment] of Object.entries(doc.comments ?? {})) {
    if (scene.cueIds.includes(comment.cueId)) {
      comments[id] = { ...comment };
    }
  }
  return {
    scene: {
      ...scene,
      cueIds: [...scene.cueIds],
      notes: scene.notes ? { ...scene.notes } : undefined,
      statusCompletedIds: scene.statusCompletedIds
        ? [...scene.statusCompletedIds]
        : undefined,
      statusSteps: scene.statusSteps?.map((s) => ({ ...s })),
      crewRoles: scene.crewRoles?.map((r) => ({
        ...r,
        people: r.people.map((p) => ({ ...p })),
      })),
    },
    cues,
    comments: Object.keys(comments).length ? comments : undefined,
    submitState,
  };
}

/** True when the fork still has a live entry under Afleverede scener. */
export function hasActiveSubmission(
  doc: ScriptDocument,
  sourceSceneId: Id,
  payload: DirectorScenePayload,
): boolean {
  const id = payload.submitState?.submittedSceneId;
  if (!id || payload.submitState?.submittedConsumed) return false;
  const scene = doc.scenes[id];
  return Boolean(scene?.submittedOfSceneId === sourceSceneId);
}

/**
 * Payload used for display: prefer the live submitted scene (notes etc.)
 * when it still exists in the vault.
 */
export function displayPayloadForFork(
  doc: ScriptDocument,
  sourceSceneId: Id,
  fork: DirectorScenePayload,
): DirectorScenePayload {
  const submittedId = fork.submitState?.submittedSceneId;
  if (
    submittedId &&
    !fork.submitState?.submittedConsumed &&
    doc.scenes[submittedId]?.submittedOfSceneId === sourceSceneId
  ) {
    const fromSubmitted = buildDirectorPayload(
      doc,
      submittedId,
      fork.submitState,
    );
    if (fromSubmitted) return fromSubmitted;
  }
  return fork;
}

/** Merge a fork payload over the live document for display/editing. */
export function documentWithDirectorFork(
  doc: ScriptDocument,
  sourceSceneId: Id,
  payload: DirectorScenePayload,
): ScriptDocument {
  const live = doc.scenes[sourceSceneId];
  if (!live) return doc;
  const scene = {
    ...payload.scene,
    id: sourceSceneId,
    episodeId: live.episodeId,
    number: live.number,
    sortOrder: live.sortOrder,
    // Keep vault markers off the program slot.
    submittedOfSceneId: undefined,
    submittedAt: undefined,
    submittedByUserId: undefined,
    submittedByName: undefined,
    archiveOfSceneId: undefined,
    archivedAt: undefined,
    archiveLabel: undefined,
    trashedAt: undefined,
  };
  return {
    ...doc,
    scenes: { ...doc.scenes, [sourceSceneId]: scene },
    cues: { ...doc.cues, ...payload.cues },
    comments: {
      ...(doc.comments ?? {}),
      ...(payload.comments ?? {}),
    },
  };
}

/** Write overlay payload content into a real scene (e.g. submitted vault scene). */
export function applyPayloadToScene(
  doc: ScriptDocument,
  targetSceneId: Id,
  payload: DirectorScenePayload,
): ScriptDocument {
  const target = doc.scenes[targetSceneId];
  if (!target) return doc;
  const oldCueIds = new Set(target.cueIds);
  const cues = { ...doc.cues };
  for (const id of oldCueIds) delete cues[id];
  for (const [id, cue] of Object.entries(payload.cues)) {
    cues[id] = { ...cue, id, sceneId: targetSceneId };
  }
  const comments = {
    ...Object.fromEntries(
      Object.entries(doc.comments ?? {}).filter(
        ([, c]) => !oldCueIds.has(c.cueId),
      ),
    ),
    ...(payload.comments ?? {}),
  };
  const ts = new Date().toISOString();
  return {
    ...doc,
    scenes: {
      ...doc.scenes,
      [targetSceneId]: {
        ...payload.scene,
        id: targetSceneId,
        episodeId: target.episodeId,
        number: target.number,
        sortOrder: target.sortOrder,
        submittedOfSceneId: target.submittedOfSceneId,
        submittedAt: target.submittedAt,
        submittedByUserId: target.submittedByUserId,
        submittedByName: target.submittedByName,
        archiveOfSceneId: undefined,
        archivedAt: undefined,
        archiveLabel: undefined,
        trashedAt: undefined,
        updatedAt: ts,
      },
    },
    cues,
    comments,
    production: { ...doc.production, updatedAt: ts },
  };
}
